// src/pages/Community/Location/LocationDetailContent.js
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import {
    LikeBar,
    ReactionBar,
    PresenceBadge,
    Discussion,
} from "../components";

const DEFAULT_GAME_MAP_IMG = "/assets/gta6-map.jpg";

export default function LocationDetailContent({
    loc,
    uid,

    /* header */
    featuredReaction,
    selectedReactionFromBar,

    /* permissions */
    isOwner,

    /* infra */
    presenceKey,
    locationId,
    basePath,
    onReactionSelect,

    /* layout */
    renderHeader = true,
    renderEngagement = true,
    renderDiscussion = true,
}) {

    /* map pins (same pattern as Missions) */
    const pins = useMemo(() => {
        if (!loc) return [];

        if (Array.isArray(loc.mapPins) && loc.mapPins.length > 0) {
            return loc.mapPins;
        }

        if (loc.coordsPct?.xPct != null && loc.coordsPct?.yPct != null) {
            return [
                {
                    xPct: Number(loc.coordsPct.xPct),
                    yPct: Number(loc.coordsPct.yPct),
                    label: loc.shortNote || loc.title || "Location",
                },
            ];
        }

        return [];
    }, [loc]);
    const mapSrc = loc.mapImage || DEFAULT_GAME_MAP_IMG;

    if (!loc) return null;

    return (
        <>
            {/* HERO */}
            <div className="locd-hero">
                {loc.coverUrl ? (
                    <img
                        src={loc.coverUrl}
                        className="locd-cover"
                        alt={loc.title || "Location"}
                        loading="lazy"
                    />
                ) : (
                    <div className="locd-cover locd-cover--placeholder">🗺️</div>
                )}

                <div className="locd-heroMeta">
                    {loc.category && <span className="locd-badge">{loc.category}</span>}
                    <span className={`locd-tag ${loc.isHidden ? "is-hidden" : "is-visible"}`}>
                        {loc.isHidden ? "Hidden" : "Visible"}
                    </span>
                </div>
            </div>

            {/* HEADER */}
            {renderHeader && (
                <div className="locd-header">
                    <h1 className="locd-title">
                        {loc.title || "Untitled location"}
                        {isOwner && (
                            <span className="locd-ownerMark" title="You created this location">
                                👑
                            </span>
                        )}
                    </h1>

                    {(featuredReaction || selectedReactionFromBar) && (
                        <div className="locd-featured-reaction" aria-hidden>
                            {featuredReaction || selectedReactionFromBar}
                        </div>
                    )}

                    <div className="locd-sub">
                        {[loc.city, loc.region, loc.difficultyToFind]
                            .filter(Boolean)
                            .join(" • ")}
                    </div>
                </div>
            )}

            {/* BODY */}
            <div className="locd-body">
                {/* LEFT */}
                <div className="locd-colLeft">
                    {loc.description && (
                        <>
                            <div className="locd-markdown">
                                <ReactMarkdown>{loc.description}</ReactMarkdown>
                            </div>
                        </>
                    )}

                    {(loc.accessReq || loc.lootHint) && (
                        <>
                            <h3 className="locd-h3">Details</h3>
                            <div className="locd-infoGrid">
                                {loc.accessReq && (
                                    <div className="locd-info">
                                        <span>Access</span>
                                        <strong>{loc.accessReq}</strong>
                                    </div>
                                )}
                                {loc.lootHint && (
                                    <div className="locd-info">
                                        <span>Loot: </span>
                                        <strong>{loc.lootHint}</strong>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {Array.isArray(loc.images) && loc.images.length > 0 && (
                        <>
                            <h3 className="locd-h3">Gallery</h3>
                            <div className="locd-gallery">
                                {loc.images.map((img, i) => (
                                    <img key={i} src={img} alt="" loading="lazy" />
                                ))}
                            </div>
                        </>
                    )}

                    {Array.isArray(loc.tags) && loc.tags.length > 0 && (
                        <>
                            <h3 className="locd-h3">Tags</h3>
                            <div className="locd-tags">
                                {loc.tags.map((t, i) => (
                                    <span key={i} className="locd-chip">
                                        #{t}
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* RIGHT */}
                <div className="locd-colRight">
                    <h3 className="locd-h3">Game Map</h3>
                    <div className="locd-mapimg-wrap">
                        <img src={mapSrc} className="locd-mapimg" alt="Game map" />
                        {pins.map((p, i) => (
                            <div
                                key={i}
                                className="locd-mappin"
                                style={{ left: `${p.xPct}%`, top: `${p.yPct}%` }}
                                title={p.label}
                            >
                                <span className="locd-mappin-dot" />
                                <span className="locd-mappin-pulse" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ENGAGEMENT */}
            {renderEngagement && (
                <div className="locd-engageWrap">
                    <div className="locd-engageRow">
                        {/* PRESENCE */}
                        <PresenceBadge presenceKey={presenceKey} entityId={locationId} />
                        <LikeBar basePath={basePath} />
                        <ReactionBar
                            basePath={basePath}
                            visibleReactions={[
                                "love",
                                "fire",
                                "insightful",
                                "cool",
                                "bug",
                                "respect",
                                "funny",
                                "applause",
                            ]}
                            onSelect={onReactionSelect}
                        />
                    </div>

                    {renderDiscussion && (
                        <Discussion
                            basePath={basePath}
                            authorId={loc.createdBy || loc.authorUid}
                        />
                    )}
                </div>
            )}
        </>
    );
}
